import { useState } from 'react'
import clsx from 'clsx'
import Image from 'next/image'
import { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { useInfiniteQuery } from '@tanstack/react-query'

/* blocks */
import Layout from '@/templates/Layout/CMLLayout'
import InfluencerGetStarted from '@/templates/Influencer/InfluencerGetStarted'
import { Navigation } from '@/templates/Influencer/InfluencerDetailsHero'
import Search from '@/templates/Search/Search'
import InfluencerTextCard from '@/molecules/InfluencerTextCard/InfluencerTextCard'

/* utils */
import { getStrapiGraphqlData } from '@/utils/api/wrapper'
import {
  allLeadersPageQuery,
  influencerPageQuery
} from '@/utils/queries/influencer'
import { INSTANT_REVALIDATION } from '@/utils/constants/revalidation'

/* assets */
import HeroImage from 'public/assets/global-marketing-leaders/influencer_2023_hero.png'

/* styling */
import styles from './_.module.css'
interface Props {
  influencerPage: InfluencerPage
}

function AllLeaders({ influencerPage }: Props) {
  const metadata = [
    {
      property: 'og:title',
      content: 'Global Marketing Leaders 2023'
    },
    {
      property: 'description',
      content:
        'Global Marketing Leaders 2023 by Pepper Content is an exclusive curation of the brightest minds who have revolutionized the world of marketing and content.'
    },
    {
      property: 'og:description',
      content:
        'Global Marketing Leaders 2023 by Pepper Content is an exclusive curation of the brightest minds who have revolutionized the world of marketing and content.'
    },
    {
      property: 'og:image',
      content:
        'https://www.peppercontent.io/assets/global-marketing-leaders/all_leaders_og.png'
    }
  ]
  const router = useRouter()
  const [filters, setFilters] = useState<{ [key: string]: string | undefined }>(
    {
      ...initialFilterState
    }
  )

  const speakerType =
    filters.type === 'leader' ? ['Global', 'Indian'] : [filters.type]

  const influencerList = useInfiniteQuery(
    ['influencers', { ...filters }],
    async ({ pageParam }) =>
      await getStrapiGraphqlData<IndividualInfluencer>(
        allLeadersPageQuery.loc?.source.body,
        {
          page: pageParam,
          speakerType,
          theme: filters.theme
        }
      ),
    {
      enabled: true,
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
      getNextPageParam: lastPage => {
        const pagination = lastPage.influencers.meta.pagination
        return pagination.pageCount > pagination.page
          ? pagination.page + 1
          : false
      }
    }
  )

  const handleScroll = async (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget
    if (scrollHeight - scrollTop - clientHeight < 1) {
      if (influencerList.hasNextPage) await influencerList.fetchNextPage()
    }
  }

  const influencerGroup =
    influencerList.data?.pages.map(data =>
      data.influencers.data.map(i => i.attributes)
    ) ?? []
  const influencers = ([] as IndividualInfluencerAttr[]).concat(
    ...influencerGroup
  )

  return (
    <Layout
      title="Global Marketing Leaders 2023"
      canonical="/global-marketing-leaders/leader/"
      meta={metadata}
    >
      <section
        className="py-8 lg:py-16 lg:px-28"
        style={{
          background: 'linear-gradient(180deg, #0F00A0 0.76%, #7818FD 50.29%)'
        }}
      >
        <header className="max-width-container">
          <Navigation
            slug={router.pathname}
            className="font-body text-base text-white"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 lg:gap-x-8 items-center | py-18 md:py-14">
            <Image
              src={HeroImage.src}
              height={HeroImage.height}
              width={HeroImage.width}
              className="h-34 w-72 md:w-[28rem] md:h-52 | object-contain | mb-4 lg:mb-10"
              alt="All Leaders Hero"
              priority
            />
            <div className="flex md:justify-end">
              <p className="font-body font-bold text-base md:text-2xl text-white | max-w-col-5 md:px-5">
                Get inspired by the best marketing leaders an icons around the
                world.
              </p>
            </div>
          </div>
        </header>

        <nav>
          <div className="max-width-container | md:py-11 | flex items-center justify-between">
            <ul className="flex overflow-x-auto  |  text-base text-white font-medium  | md:mx-auto lg:mx-0 space-x-6 lg:space-x-8">
              {categoryList.map(category => (
                <li key={category.title}>
                  <button
                    onClick={() =>
                      setFilters({ ...filters, type: category.value })
                    }
                    className={clsx(
                      'block uppercase | whitespace-nowrap | py-4 lg:py-5 | focus:outline-none',
                      filters.type === category.value &&
                        'border-b-2 border-white'
                    )}
                  >
                    {category.title}
                  </button>
                </li>
              ))}
            </ul>

            {/* <div className="hidden xl:block">
              <InfluencersFilterDropdown
                values={filters}
                themes={themeList}
                handleFilterChange={(key, value) =>
                  setFilters({ ...filters, [key]: value })
                }
              />
            </div> */}
          </div>
        </nav>

        {/* <div className="xl:hidden | mx-auto md:mx-0 p-6">
          <InfluencersFilterDropdown
            values={filters}
            themes={themeList}
            handleFilterChange={(key, value) =>
              setFilters({ ...filters, [key]: value })
            }
          />
        </div> */}

        <div className="max-width-container mx-auto p-6 |  grid gap-8 xl:grid-cols-12">
          {/* <div className="hidden xl:block | xl:col-span-3 | mb-16">
            <ul>
              {contentTypes.map(item => (
                <li key={item.id}>
                  <button
                    className={clsx(
                      'flex items-center | text-blue-web-60 bg-blue-10 text-left | rounded-lg | min-h-8 w-full | mb-5 px-3 py-2 | hover:outline-2 hover:outline-yellow-30 hover:outline hover:shadow-2 | transition-all | focus:outline-2 focus:border-yellow-30 focus:bg-blue-10 |  ease-in',
                      item.slug === filters.contentType &&
                        'text-blue-50 bg-blue-10'
                    )}
                    style={{ maxWidth: 370, fontSize: 18 }}
                    onClick={() => handleFilterChange('contentType', item.slug)}
                  >
                    {item.name}
                  </button>
                </li>
              ))}
            </ul>
          </div> */}

          {/* Influencer listing */}
          <div className="flex flex-wrap flex-col xl:col-span-12">
            <div className="lg:max-w-sm mb-8">
              <Search />
            </div>
            <div
              className={clsx(
                'grid gap-4 md:gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:col-span-9 pr-2 | max-h-[570px] overflow-y-scroll snap-y snap-mandatory',
                styles.scrollbar
              )}
              onScroll={e => handleScroll(e)}
            >
              {influencers.map(influencer => {
                return (
                  <article key={influencer.name}>
                    <InfluencerTextCard
                      name={influencer.name}
                      slug={`/global-marketing-leaders/${influencer.slug.trim()}/`}
                      designation={influencer.designation}
                      image={influencer.profileImage}
                    />
                  </article>
                )
              })}
              {influencers.length === 0 && (
                <div className="col-span-full flex items-center justify-center flex-col">
                  <h3 className="text-white text-2xl mb-4">No leaders found</h3>
                  <button
                    className="btn-secondary btn-size-default"
                    onClick={() => setFilters(initialFilterState)}
                  >
                    Clear Filters?
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <InfluencerGetStarted
        primaryHeadline={influencerPage.getStarted.title ?? ''}
        subText={influencerPage.getStarted.description ?? ''}
        image={influencerPage.getStarted.multimedia?.media?.data?.attributes}
        primaryCta={influencerPage.getStarted.link}
        mode="full_width"
      />
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const result = await getStrapiGraphqlData<InfluencerPageQuery>(
    influencerPageQuery.loc?.source.body
  )

  const page = result?.influencerPage?.data?.attributes
  // redirect to 404 page
  if (!page) {
    return {
      notFound: true
    }
  }
  return {
    props: {
      influencerPage: page
    },
    revalidate: INSTANT_REVALIDATION
  }
}

const categoryList = [
  {
    title: 'Global Leaders',
    value: 'Global'
  },
  {
    title: 'Indian Leaders',
    value: 'Indian'
  },
  {
    title: 'All Leaders',
    value: 'leader'
  }
]

const initialFilterState = {
  type: 'leader',
  theme: undefined
}

export default AllLeaders
